import {
  CLAUSE_CODE_CPI_ELECTRICIAN_QLD,
  CLAUSE_CODE_CPI_PLUMBER_VICTORIA,
  CLAUSE_CODE_NAME_OF_LANDLORD,
} from '../../constants/domain-specific';
import type { BusinessDetails } from '../../slices/businessDetails';
import type { CoverDetails } from '../../slices/coverDetails';
import type { ExtraInfo } from '../../slices/extraInfo';
import type { Quote } from '../../slices/quote';
import { ClassType, DynamicCoverQuestion, PlumbingLicence } from '../../types/cover';
import { Clause, RiskLocation } from '../../types/policy';
import { OccupationsListItem } from '../../types/refData';
import { RiskClassCode } from '../../types/risk';
import { Nullable } from '../../types/utils';
import { RiskMetaType } from './getRiskMeta';

const landlordNameToClauseWordingVariables = ({
  landlordName,
  dynamicCoverQuestions,
  businessLocation,
}: {
  landlordName: string;
  dynamicCoverQuestions: DynamicCoverQuestion[] | null;
  businessLocation: RiskLocation;
}) =>
  dynamicCoverQuestions
    ?.filter((q) => q.cover === ClassType.LIABILITY)
    .flatMap((q) => q.options)
    .filter((q) => q.clauseCode === CLAUSE_CODE_NAME_OF_LANDLORD)
    .map((question) => {
      if (question.questionId === 'NAME_OF_LANDORD_LNE1') {
        return {
          ...question,
          data: landlordName,
        };
      }
      if (question.questionId === 'SITUATION(S)_1') {
        return {
          ...question,
          data: `${businessLocation.addressLine1},${businessLocation.city},${businessLocation.stateCode},${businessLocation.postalCode}`.substring(
            0,
            question.length
          ),
        };
      }
      return {
        ...question,
        data: '',
      };
    });

const plumbingLicenceToClauseWordingVariables = ({
  licences,
  dynamicCoverQuestions,
}: {
  licences: PlumbingLicence[] | null;
  dynamicCoverQuestions: DynamicCoverQuestion[] | null;
}) =>
  dynamicCoverQuestions
    ?.filter((q) => q.cover === ClassType.LIABILITY)
    .flatMap((q) => q.options)
    .filter((q) => q.clauseCode === CLAUSE_CODE_CPI_PLUMBER_VICTORIA)
    .map((question) => {
      const match = /PLUMBER (\d+) (NAME|LICENCE NO)/.exec(question.title);
      if (!match)
        return {
          ...question,
          data: '',
        };
      const licence = licences?.[Number(match[1]) - 1];
      if (!licence)
        return {
          ...question,
          data: '',
        };
      return {
        ...question,
        data: match[2] === 'NAME' ? licence.name : licence.number,
      };
    });

export const getLiabilityRisk = ({
  riskMeta,
  coverDetails,
  extraInfo,
  isPropertyOwner,
  businessLocation,
  businessDetails,
  quote,
  propertyOwnerOccupation,
}: {
  riskMeta: RiskMetaType;
  coverDetails: Nullable<CoverDetails>;
  extraInfo: Nullable<ExtraInfo>;
  isPropertyOwner: boolean;
  businessLocation: RiskLocation;
  businessDetails: Partial<BusinessDetails>;
  quote: Nullable<Quote>;
  propertyOwnerOccupation: OccupationsListItem | undefined;
}) => {
  const liabilityClauses: Clause[] = [];
  if (coverDetails.cpiQLD === true)
    liabilityClauses.push({
      clauseWording: [
        {
          riskClass: RiskClassCode.Liability,
          premiumClass: ClassType.LIABILITY,
          clauseCode: CLAUSE_CODE_CPI_ELECTRICIAN_QLD,
        },
      ],
    });
  if (coverDetails.cpiVIC === true)
    liabilityClauses.push({
      clauseWording: [
        {
          riskClass: RiskClassCode.Liability,
          premiumClass: ClassType.LIABILITY,
          clauseCode: CLAUSE_CODE_CPI_PLUMBER_VICTORIA,
          clauseWordingVariables: plumbingLicenceToClauseWordingVariables({
            licences: coverDetails.plumbingLicences,
            dynamicCoverQuestions: coverDetails.dynamicCoverQuestions,
          }),
        },
      ],
    });
  if (extraInfo.landlordName) {
    liabilityClauses.push({
      clauseWording: [
        {
          riskClass: RiskClassCode.Liability,
          premiumClass: ClassType.LIABILITY,
          clauseCode: CLAUSE_CODE_NAME_OF_LANDLORD,
          clauseWordingVariables: landlordNameToClauseWordingVariables({
            landlordName: extraInfo.landlordName,
            dynamicCoverQuestions: coverDetails.dynamicCoverQuestions,
            businessLocation,
          }),
        },
      ],
    });
  }
  return {
    ...riskMeta,
    ...(liabilityClauses.length && {
      clauses: liabilityClauses,
    }),
    riskClassCode: RiskClassCode.Liability,
    riskDetail: {
      riskBPKLiability: {
        occupationCode:
          (isPropertyOwner
            ? propertyOwnerOccupation?.code
            : businessDetails.occupation?.anzsicCode) ?? '',
        occupationDescription:
          (isPropertyOwner
            ? propertyOwnerOccupation?.desc
            : businessDetails.occupation?.occupation) ?? '',
        business:
          (isPropertyOwner
            ? propertyOwnerOccupation?.desc
            : businessDetails.occupation?.occupation) ?? '',
        numberOfEmployees: businessDetails.employeeCount ?? 0,
        limitOfLiability: coverDetails.liabilityLimit ?? 0,
        liabilityExcess: quote.levelOfExcess!,
        annualTurnover: businessDetails.annualTurnover ?? 0,
        propertyOwnersDesc: 'NO',
        propertyValue: businessDetails.propertyValue ?? 0,
        paymentToContractors: coverDetails.useOfContractors ?? 0,
      },
    },
  };
};
